import { useCallback } from "react";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

import { fetchFromBase } from "@/api/fetchFromBase";
import { useAuthStore } from "@/stores/use-auth-store";
import { captureCurrentRoute } from "@/utils/auth-route";
import { type User, useAuthCheckCore } from "shared-components";

interface UseAuthCheckOptions {
  requireAuth?: boolean;
}

type AuthSession = {
  user?: User;
};

export function useAuthCheck(options?: UseAuthCheckOptions) {
  const { setAuthenticated, setOnAuthRoute } = useAuthStore();
  const fetchSession = useCallback(() => fetchFromBase("auth/me"), []);

  const handleAuthenticated = useCallback(
    ({ session }: { session: AuthSession }) => {
      setAuthenticated(true, undefined, session?.user);
    },
    [setAuthenticated],
  );

  const handleUnauthenticated = useCallback(
    ({ router, requireAuth }: { router: AppRouterInstance; requireAuth: boolean }) => {
      setAuthenticated(false);

      if (requireAuth) {
        setOnAuthRoute(captureCurrentRoute());
        router.replace("/login");
      }
    },
    [setAuthenticated, setOnAuthRoute],
  );

  const handleError = useCallback(
    ({ router, requireAuth, error }: { router: AppRouterInstance; requireAuth: boolean; error?: unknown }) => {
      setAuthenticated(false);

      if (requireAuth) {
        setOnAuthRoute(captureCurrentRoute());
        router.replace("/login");
      }

      if (error) {
        console.error("Auth check failed:", error);
      }
    },
    [setAuthenticated, setOnAuthRoute],
  );

  useAuthCheckCore<AuthSession>({
    requireAuth: options?.requireAuth,
    fetchSession,
    onAuthenticated: handleAuthenticated,
    onUnauthenticated: handleUnauthenticated,
    onError: handleError,
  });
}
